import {
  Stack,
  Paper,
  Text,
  Table,
  Button,
  Group,
  Textarea,
  ActionIcon,
  useMantineTheme,
  Tooltip,
  Badge,
  Title,
  Accordion,
  LoadingOverlay,
  Popover,
  NumberInput,
  Modal,
  TextInput,
} from "@mantine/core";
import { useState, useEffect } from "react";
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconArrowBack,
  IconPrinter,
  IconSignature,
  IconLockOpen,
  IconRefresh,
  IconDownload,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { RealisedWorkPhaseItemDto } from "../../../types/RealisedWorkPhaseItem";
import {
  getRealisedWorkPhases,
  deleteWorkItem,
  updateWorkItem,
  createWorkItem,
} from "../../../services/objectmodelservice";
import {
  updatePolyline,
  updatePolypoint,
  updatePolygon,
  getPolylines,
  getPolygons,
  getPolypoints,
} from "../../../services/MapComponentsService";
// import { searchGeometries } from '../../../services/GeometrySearchService';
import { UpdateGeometryRequest } from "../../../types/Geometry";
import AddEditWorkStepModal from "./modals/AddEditWorkStepModal";
import useTransientStore from "../../../data/transcientStore";
import usePermissions from "../../../hooks/usePermissions";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { MeasurementReportPDF } from "../../pdf/MeasurementReportPDF";
import {
  sanitizeForFilename,
  generateTimestamp,
} from "../../../utilities/PdfUtils";
import { getContractById } from "../../../services/ContractService";
import {
  startSigning,
  getSigningHistory,
  releaseSigningByObjectId,
  downloadSignedFileForUser,
  getSigningPrecheck,
} from "../../../services/SigningService";
import type { SigningStatusDto } from "../../../services/SigningService";
import { useTranslation } from "../../../hooks/useTranslation";

interface RealisedWorkStepsTabProps {
  form: any;
  canModify?: boolean;
}

export default function RealisedWorkStepsTab({
  form,
  canModify = true,
}: RealisedWorkStepsTabProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 335,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const theme = useMantineTheme();
  const headerColor = theme.colors.gray[2];
  const headerText = theme.colors.dark[7];

  const [items, setItems] = useState<RealisedWorkPhaseItemDto[]>([]);
  const [workStepModalOpened, setWorkStepModalOpened] = useState(false);
  const [editingWorkStep, setEditingWorkStep] = useState<any | null>(null);
  const [editingFetchedItem, setEditingFetchedItem] = useState<any | null>(
    null,
  );
  const [isEditingOther, setIsEditingOther] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingWorkItemId, setDeletingWorkItemId] = useState<number | null>(
    null,
  );
  const [editingCell, setEditingCell] = useState<{
    priceItemId: number;
    field: "realisedAmount" | "realisedArea" | "realisedWeight";
    value: number;
  } | null>(null);
  const [pendingChanges, setPendingChanges] = useState<
    Map<
      number,
      {
        workItemId: number;
        priceItemId: number;
        name: string;
        unitName: string;
        unitPrice: number;
        realisedAmount: number;
        realisedArea: number | null;
        realisedWeight: number | null;
        description: string | null;
      }
    >
  >(new Map());
  const [validationErrors, setValidationErrors] = useState<Set<number>>(
    new Set(),
  );
  const [originalComment, setOriginalComment] = useState<string>("");
  const [signingModalOpened, setSigningModalOpened] = useState(false);
  const [signingHistory, setSigningHistory] = useState<SigningStatusDto[]>([]);
  const [isSigning, setIsSigning] = useState(false);
  const [releaseModalOpened, setReleaseModalOpened] = useState(false);
  const [releaseComment, setReleaseComment] = useState("");
  const [isReleasing, setIsReleasing] = useState(false);

  // Get current user info from store
  const currentUser = useTransientStore((state) => state.currentUser);
  const currentUserId = useTransientStore((state) => state.currentUserId);

  // Get client system settings for the address system
  const clientSystemSettings = useTransientStore(
    (state) => state.clientSystemSettings,
  );

  // Get approved status ids
  const cityObjectApprovedStatusIds = (() => {
    const value = clientSystemSettings.get("CITY_OBJECT_APPROVED_STATUS_IDS");
    if (!value) return [];
    return value
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s))
      .filter((n) => !isNaN(n));
  })();

  // Permissions
  const { checkAccess } = usePermissions();
  const canModifyWorkSteps = checkAccess("WORK_IMPLEMENTED_MODIFY");
  const canShowInternalComment = checkAccess(
    "CITY_OBJECT_SHOW_INTERNAL_COMMENT",
  );
  const canEnableSigning = checkAccess("CITY_OBJECT_ENABLE_SIGNING");

  // Derive IDs from the shared form
  const contractId: number | undefined = (() => {
    const explicit = form?.values?.contractId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const job = form?.values?.job;
    if (typeof job === "string" && job.length > 0) {
      const parsed = parseInt(job, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  })();

  const objectId: number | undefined = (() => {
    const explicit = form?.values?.objectId;
    if (typeof explicit === "number" && explicit > 0) return explicit;
    const objId = form?.values?.id;
    if (typeof objId === "number" && objId > 0) return objId;
    return undefined;
  })();

  // const headerInfo: string | undefined = (() => {
  //     const v = form?.values ?? {};
  //     const contractName = typeof v.contractName === "string" && v.contractName.trim().length
  //         ? v.contractName.trim()
  //         : undefined;
  //     const objectName = typeof v.name === "string" && v.name.trim().length
  //         ? v.name.trim()
  //         : undefined;

  //     if (contractName && objectName) {
  //         return `${contractName} / ${objectName}`;
  //     } else if (contractName) {
  //         return contractName;
  //     } else if (objectName) {
  //         return objectName;
  //     }
  //     return undefined;
  // })();

  // Check if object is in approved state (locked for editing)
  const status = form?.values?.status;
  const statusId = typeof status === "string" ? parseInt(status, 10) : status;
  const isApprovedAndLocked =
    statusId !== undefined &&
    !isNaN(statusId) &&
    cityObjectApprovedStatusIds.includes(statusId);

  // Check if object is locked by signing
  const signingLocked =
    form?.values?.signingLocked === 1 || form?.values?.signingLocked === true;

  // Determine if editing is disabled and the appropriate tooltip message
  const isEditingDisabled =
    !canModify || isApprovedAndLocked || signingLocked || !canModifyWorkSteps;
  const getDisabledTooltip = () => {
    if (!canModify || !canModifyWorkSteps) {
      return (
        getTranslation("LABEL_NO_PERMISSION_MODIFY_OBJECT") ||
        "You don't have permission to modify this object"
      );
    }
    if (isApprovedAndLocked && signingLocked) {
      return (
        getTranslation("LABEL_APPROVED_AND_SIGNING_LOCKED") ||
        "Approved and signing locked"
      );
    } else if (isApprovedAndLocked) {
      return getTranslation("LABEL_AT_APPROVED_STAGE") || "At approved stage";
    } else if (signingLocked) {
      return getTranslation("LABEL_SIGNING_LOCKED") || "Signing locked";
    }
    return "";
  };

  // Subscribe to refresh trigger for cross-tab synchronization
  const workItemsRefreshTrigger = useTransientStore(
    (state) => state.workItemsRefreshTrigger,
  );

  // Ensure consistent column widths
  const renderColGroup = () => (
    <colgroup>
      <col style={{ width: "220px" }} />
      <col style={{ width: "70px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "100px" }} />
      <col style={{ width: "80px" }} />
      <col style={{ width: "80px" }} />
    </colgroup>
  );

  useEffect(() => {
    const loadData = async () => {
      if (!contractId || !objectId) return;

      setIsLoading(true);
      try {
        // Use the new dedicated endpoint
        const data = await getRealisedWorkPhases(contractId, objectId);
        setItems(data);
        setOriginalComment(form?.values?.implementedComment || "");
      } catch (error) {
        console.error("Failed to load realised work steps:", error);
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, objectId, workItemsRefreshTrigger]);

  const reloadItems = async () => {
    if (!contractId || !objectId) return;

    setIsLoading(true);
    try {
      const data = await getRealisedWorkPhases(contractId, objectId);
      setItems(data);
    } catch (error) {
      console.error("Failed to reload realised work steps:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWorkStep = () => {
    setEditingWorkStep(null);
    setEditingFetchedItem(null);
    setIsEditingOther(false);
    setWorkStepModalOpened(true);
  };

  const mapFetchedItemToWorkStep = (it: RealisedWorkPhaseItemDto): any => {
    return {
      id: it.workItemId,
      typeOfCoating: it.name,
      unit: it.unitName,
      amount: it.realisedAmount,
      surfaceArea: it.realisedArea ?? 0,
      tons: it.realisedWeight ?? 0,
      unitPrice: Number(it.unitPrice),
      priceC: Number(it.price),
    };
  };

  const handleEditFetchedItem = (it: RealisedWorkPhaseItemDto) => {
    setEditingFetchedItem({
      workItemId: it.workItemId,
      priceItemId: it.priceItemId,
      name: it.name,
      unitName: it.unitName,
      unitPrice: it.unitPrice,
      itemCount: it.realisedAmount,
      itemArea: it.realisedArea,
      itemWeight: it.realisedWeight,
      totalPrice: it.price,
      description: it.description,
      orderInGroup: 0,
      itemNumber: 0,
      itemSubnumber: 0,
      itemCode: "",
    });

    // Determine if "Others" group
    const idx = items.findIndex((x) => x.workItemId === it.workItemId);
    let groupHeaderName: string | null = null;
    for (let i = idx - 1; i >= 0; i--) {
      if (items[i].isGroupHeader) {
        groupHeaderName = items[i].name;
        break;
      }
    }
    setIsEditingOther(groupHeaderName === "Others");
    setEditingWorkStep(mapFetchedItemToWorkStep(it));
    setWorkStepModalOpened(true);
  };

  const confirmAndDeleteFetchedItem = (workItemId: number) => {
    setDeletingWorkItemId(workItemId);
  };

  const performDeleteWorkItem = async (workItemId: number) => {
    setDeletingWorkItemId(null);
    if (!contractId || !objectId) {
      //console.log('Contract ID or Object ID is missing. Cannot delete work item.');
      return;
    }

    // Show loading overlay during the delete operation
    setIsLoading(true);
    try {
      await deleteWorkItem(workItemId, contractId, objectId);
      await reloadItems();
      // Trigger refresh for other tabs
      useTransientStore.getState().triggerWorkItemsRefresh();
    } catch (error) {
      console.error("Failed to delete work item", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveWorkStep = async (values: any) => {
    setWorkStepModalOpened(false);

    setEditingWorkStep(null);
    setEditingFetchedItem(null);

    // Reload items first to get fresh data from database
    await reloadItems();

    // Trigger refresh for other tabs
    useTransientStore.getState().triggerWorkItemsRefresh();

    setPendingChanges(new Map());
    setValidationErrors(new Set());
  };

  const handleCellUpdate = async (
    item: RealisedWorkPhaseItemDto,
    field: "realisedAmount" | "realisedArea" | "realisedWeight",
    value: number,
  ) => {
    try {
      const key = item.priceItemId!;
      const existing = pendingChanges.get(key) || {
        workItemId: item.workItemId,
        priceItemId: item.priceItemId!,
        name: item.name,
        unitName: item.unitName,
        unitPrice: item.unitPrice,
        realisedAmount: item.realisedAmount,
        realisedArea: item.realisedArea ?? null,
        realisedWeight: item.realisedWeight ?? null,
        description: item.description ?? null,
      };

      // Update the specific field
      if (field === "realisedAmount") {
        existing.realisedAmount = value;
      } else if (field === "realisedArea") {
        existing.realisedArea = value;
      } else if (field === "realisedWeight") {
        existing.realisedWeight = value;
      }

      // Check if all values match the original item (no actual changes)
      const amountMatches = existing.realisedAmount === item.realisedAmount;
      const areaMatches =
        (existing.realisedArea ?? 0) === (item.realisedArea ?? 0);
      const weightMatches =
        (existing.realisedWeight ?? 0) === (item.realisedWeight ?? 0);

      const newPending = new Map(pendingChanges);

      if (amountMatches && areaMatches && weightMatches) {
        // All values match original - remove from pending changes
        newPending.delete(key);
        // Also clear any validation errors for this item
        if (validationErrors.has(key)) {
          const newErrors = new Set(validationErrors);
          newErrors.delete(key);
          setValidationErrors(newErrors);
        }
      } else {
        // Values differ from original - keep in pending changes
        newPending.set(key, existing);
      }

      setPendingChanges(newPending);
      setEditingCell(null);
    } catch (error) {
      console.error("Failed to update cell value:", error);
    }
  };

  const getPendingValue = (
    priceItemId: number | null | undefined,
    field: "realisedAmount" | "realisedArea" | "realisedWeight",
    originalValue: number,
  ) => {
    if (!priceItemId || !pendingChanges.has(priceItemId)) {
      return originalValue;
    }
    const pending = pendingChanges.get(priceItemId);
    return pending?.[field] ?? originalValue;
  };

  const hasPendingChanges = (
    priceItemId: number | null | undefined,
    workItemId: number,
  ) => {
    if (!priceItemId) return false;
    return pendingChanges.has(priceItemId);
  };

  const discardRowChanges = (priceItemId: number) => {
    const newPending = new Map(pendingChanges);
    newPending.delete(priceItemId);
    setPendingChanges(newPending);

    // Also clear validation errors for this item
    if (validationErrors.has(priceItemId)) {
      const newErrors = new Set(validationErrors);
      newErrors.delete(priceItemId);
      setValidationErrors(newErrors);
    }
  };

  const calculateTotalCost = () => {
    return items
      .filter((item) => item.workItemId > 0)
      .reduce((total, item) => total + item.price, 0);
  };

  const calculateTotalChange = () => {
    return items
      .filter((item) => item.workItemId > 0)
      .reduce((total, item) => total + item.costChange, 0);
  };

  const saveData = async () => {
    if (!objectId) {
      //console.log('Object ID is missing. Cannot save data.');
      return;
    }

    // Validate pending changes before saving
    if (pendingChanges.size > 0) {
      const invalidItems = Array.from(pendingChanges.values()).filter(
        (change) => change.realisedAmount <= 0,
      );

      if (invalidItems.length > 0) {
        const invalidPriceItemIds = new Set(
          invalidItems.map((item) => item.priceItemId),
        );
        setValidationErrors(invalidPriceItemIds);
        return;
      }
    }

    // Clear validation errors if validation passed
    setValidationErrors(new Set());

    setIsSaving(true);
    try {
      // First, save any pending realised work items
      if (pendingChanges.size > 0) {
        const pendingArray = Array.from(pendingChanges.values());
        const createItems = pendingArray.filter(
          (change) => change.workItemId === 0,
        );
        const updateItems = pendingArray.filter(
          (change) => change.workItemId > 0,
        );

        // Create new work items
        const createPromises = createItems.map((change) => {
          const payload = {
            priceItemId: change.priceItemId,
            name: change.name,
            unitName: change.unitName,
            unitPrice: change.unitPrice,
            itemCount: change.realisedAmount,
            itemArea: change.realisedArea,
            itemWeight: change.realisedWeight,
            description: change.description,
            contractId,
            objectId,
            workItemType: 1, // Realised
          };
          return createWorkItem(payload, currentUserId);
        });

        // Update existing work items
        const updatePromises = updateItems.map((change) => {
          const payload = {
            workItemId: change.workItemId,
            priceItemId: change.priceItemId,
            name: change.name,
            unitName: change.unitName,
            unitPrice: change.unitPrice,
            itemCount: change.realisedAmount,
            itemArea: change.realisedArea,
            itemWeight: change.realisedWeight,
            description: change.description,
            contractId,
            objectId,
            workItemType: 1, // Realised
          };
          return updateWorkItem(change.workItemId, payload, currentUserId);
        });

        await Promise.all([...createPromises, ...updatePromises]);

        // Reload items to show updated data first
        await reloadItems();

        // Trigger refresh for other tabs
        useTransientStore.getState().triggerWorkItemsRefresh();

        // Clear pending changes after reload completes
        setPendingChanges(new Map());
      }

      const geometryType = form?.values?.drawingType?.toLowerCase() || "";

      const updatePayload: UpdateGeometryRequest = {
        perceptionId: objectId,
        implementedComment: form?.values?.implementedComment || "",
        modifierUserId: currentUserId,
        modifierUserName: currentUser,
      };

      // Call the appropriate update endpoint based on geometry type
      if (geometryType.includes("line") || geometryType.includes("polyline")) {
        await updatePolyline(updatePayload);
      } else if (geometryType.includes("point")) {
        await updatePolypoint(updatePayload);
      } else if (geometryType.includes("polygon")) {
        await updatePolygon(updatePayload);
      } else {
        throw new Error(`Unknown geometry type: ${geometryType}`);
      }

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_SAVED_SUCCESSFULLY") || "Saved successfully!",
        color: "green",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-success",
        },
      });

      // Update original comment to the newly saved value
      setOriginalComment(form?.values?.implementedComment || "");

      // Refresh geometries after update
      try {
        const state = useTransientStore.getState();
        const { selectedAddressSystem } = state;

        if (selectedAddressSystem?.id && contractId) {
          const [polylines, polygons, polypoints] = await Promise.all([
            getPolylines(selectedAddressSystem.id, contractId),
            getPolygons(selectedAddressSystem.id, contractId),
            getPolypoints(selectedAddressSystem.id, contractId),
          ]);

          state.setCurrentPolylines(polylines);
          state.setCurrentPolygons(polygons);
          state.setCurrentPolypoints(polypoints);
        }
      } catch (error) {
        console.error("Failed to refresh geometries after save:", error);
      }

      // Update form values with fresh data
      const store = useTransientStore.getState();
      const allGeometries = [
        ...store.currentPolylines,
        ...store.currentPolygons,
        ...store.currentPolypoints,
      ];

      const updatedGeometry = allGeometries.find((geo) => geo.id === objectId);

      if (updatedGeometry) {
        form.setFieldValue(
          "implementedComment",
          updatedGeometry.implementedComment || "",
        );
        form.setFieldValue("modifier", updatedGeometry.modifier || currentUser);
        form.setFieldValue(
          "modified",
          updatedGeometry.modified
            ? new Date(updatedGeometry.modified)
            : new Date(),
        );
      }
    } catch (error) {
      console.error("Error saving:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const printMeasurementReport = async () => {
    try {
      // Show loading notification
      const loadingNotification = notifications.show({
        loading: true,
        title: "Generating PDF",
        message: "Preparing measurement report...",
        autoClose: false,
        withCloseButton: false,
      });

      // Fetch contract data
      let contractData = null;
      if (contractId) {
        try {
          const selectedAddressSystem =
            useTransientStore.getState().selectedAddressSystem;
          const addressSystemId =
            form?.values?.addressSystemId || selectedAddressSystem?.id || 1;
          contractData = await getContractById(contractId, addressSystemId);
        } catch (error) {
          console.error("Failed to fetch contract data:", error);
          // Continue without contract data - will use form fallbacks
        }
      }

      // Generate PDF
      const blob = await pdf(
        <MeasurementReportPDF
          items={items}
          totalCost={calculateTotalCost()}
          totalChange={calculateTotalChange()}
          form={form}
          objectId={objectId}
          contractId={contractId}
          contract={contractData}
          getTranslation={getTranslation}
        />,
      ).toBlob();

      // Hide loading notification
      notifications.hide(loadingNotification);

      // Trigger download
      const location =
        form?.values?.streetName || form?.values?.name || "unknown";
      const sanitizedLocation = sanitizeForFilename(location);
      const timestamp = generateTimestamp();
      const fileName = `measurement_report_${sanitizedLocation}_${timestamp}.pdf`;
      saveAs(blob, fileName);

      // Show success notification
      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_MEASUREMENT_REPORT_GENERATED") ||
          "Measurement report generated successfully!",
        color: "green",
        autoClose: 5000,
        position: "top-center",
      });
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_MEASUREMENT_REPORT_FAILED") ||
          "Failed to generate measurement report. Please try again.",
        color: "red",
        autoClose: 5000,
        position: "top-center",
      });
    }
  };

  const handleSignDocumentPrecheck = async () => {
    if (!objectId) return;

    const addressSystemId =
      form?.values?.addressSystemId || addrIdFromStore || 1;

    try {
      const result = await getSigningPrecheck(objectId, addressSystemId);

      if (!result.canSign) {
        const failedChecks: string[] = [];
        if (!result.checks.statusAllowed.passed)
          failedChecks.push(result.checks.statusAllowed.message);
        if (!result.checks.hasDiaryEntries.passed)
          failedChecks.push(result.checks.hasDiaryEntries.message);
        if (!result.checks.checkedToInvoice.passed)
          failedChecks.push(result.checks.checkedToInvoice.message);

        notifications.show({
          title:
            getTranslation("TITLE_SIGNING_PRECONDITIONS_NOT_MET") ||
            "Signing preconditions not met",
          message: failedChecks.join(". "),
          color: "orange",
          autoClose: 10000,
        });
        return;
      }

      setSigningModalOpened(true);
    } catch (error: any) {
      console.error("Failed to run signing prechecks:", error);
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_SIGNING_PRECHECK_FAILED") ||
          "Failed to verify signing preconditions. Please try again.",
        color: "red",
      });
    }
  };

  const handleSignDocument = async () => {
    try {
      setIsSigning(true);

      if (!objectId || !contractId) {
        notifications.show({
          title: getTranslation("TITLE_ERROR") || "Error",
          message:
            getTranslation("MESSAGE_MISSING_OBJECT_OR_CONTRACT") ||
            "Missing object or contract ID",
          color: "red",
        });
        return;
      }

      // Get signer information from form
      const foremanName =
        form?.values?.foreman?.name || form?.values?.foremanName || "";
      const foremanEmail =
        form?.values?.foreman?.email || form?.values?.foremanEmail || "";
      const contractorForemanName =
        form?.values?.contractorForeman?.name ||
        form?.values?.contractorForemanName ||
        "";
      const contractorForemanEmail =
        form?.values?.contractorForeman?.email ||
        form?.values?.contractorForemanEmail ||
        "";

      if (
        !foremanName ||
        !foremanEmail ||
        !contractorForemanName ||
        !contractorForemanEmail
      ) {
        notifications.show({
          title:
            getTranslation("TITLE_MISSING_INFORMATION") ||
            "Missing Information",
          message:
            getTranslation("MESSAGE_FOREMAN_DETAILS_REQUIRED") ||
            "Please ensure foreman and contractor foreman details are filled in the form",
          color: "orange",
        });
        return;
      }

      // Generate PDF
      let contractData = null;
      if (contractId) {
        try {
          const selectedAddressSystem =
            useTransientStore.getState().selectedAddressSystem;
          const addressSystemId =
            form?.values?.addressSystemId || selectedAddressSystem?.id || 1;
          contractData = await getContractById(contractId, addressSystemId);
        } catch (error) {
          console.error("Failed to fetch contract data:", error);
        }
      }

      const blob = await pdf(
        <MeasurementReportPDF
          items={items}
          totalCost={calculateTotalCost()}
          totalChange={calculateTotalChange()}
          form={form}
          objectId={objectId}
          contractId={contractId}
          contract={contractData}
          getTranslation={getTranslation}
        />,
      ).toBlob();

      // Convert blob to base64
      const arrayBuffer = await blob.arrayBuffer();
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          "",
        ),
      );

      // console.log('=== About to call startSigning API ===');
      // console.log('ObjectId:', objectId);
      // console.log('ContractId:', contractId);
      // console.log('PDF Base64 length:', base64.length);
      // console.log('Primary Signer:', foremanName, foremanEmail);
      // console.log('Secondary Signer:', contractorForemanName, contractorForemanEmail);

      // Call signing API
      const addressSystemId =
        form?.values?.addressSystemId || addrIdFromStore || 1;
      const objectName = form?.values?.name || form?.values?.location || "";
      const response = await startSigning({
        objectId,
        contractId,
        objectName,
        contractName: contractData?.name || "",
        currentUserId: currentUserId || 0,
        addressSystemId,
        primarySigner: contractorForemanName,
        primaryEmail: contractorForemanEmail,
        secondarySigner: foremanName,
        secondaryEmail: foremanEmail,
        pdfBase64: base64,
      });

      //console.log('Signing API response:', response);

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_DOCUMENT_SENT_FOR_SIGNING") ||
          `Document sent for signing! Request ID: ${response.requestId}`,
        color: "green",
      });

      setSigningModalOpened(false);

      // Refresh signing history to show new request
      await loadSigningHistory();

      // Update form to set signing_locked to 1
      if (form) {
        form.setFieldValue("signingLocked", 1);
      }
    } catch (error: any) {
      console.error("Failed to start signing:", error);
      console.error(
        "Error details:",
        error?.response?.data || error?.message || error,
      );

      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        error?.message ||
        getTranslation("MESSAGE_SIGNING_FAILED") ||
        "Failed to send document for signing. Please try again.";

      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message: errorMessage,
        color: "red",
        autoClose: 10000,
      });
    } finally {
      setIsSigning(false);
    }
  };

  const handleReleaseSigning = async () => {
    try {
      if (!releaseComment.trim()) {
        notifications.show({
          title: getTranslation("TITLE_ERROR") || "Error",
          message:
            getTranslation("MESSAGE_RELEASE_SIGNING_COMMENT_REQUIRED") ||
            "Please provide a comment explaining why the signing is being released",
          color: "orange",
        });
        return;
      }

      if (!objectId) {
        notifications.show({
          title: getTranslation("TITLE_ERROR") || "Error",
          message:
            getTranslation("MESSAGE_MISSING_OBJECT_ID") || "Missing object ID",
          color: "red",
        });
        return;
      }

      setIsReleasing(true);

      await releaseSigningByObjectId(objectId, releaseComment);

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_SIGNING_RELEASED") ||
          "Signing released successfully",
        color: "green",
      });

      setReleaseModalOpened(false);
      setReleaseComment("");

      // Refresh signing history to show release
      await loadSigningHistory();

      // Refresh form to get updated signing_locked status
      if (form) {
        form.setFieldValue("signingLocked", 0);
      }
    } catch (error: any) {
      console.error("Failed to release signing:", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        getTranslation("MESSAGE_RELEASE_SIGNING_FAILED") ||
        "Failed to release signing. Please try again.";

      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsReleasing(false);
    }
  };

  // Function to load signing history
  const loadSigningHistory = async () => {
    if (!objectId) return;
    try {
      const history = await getSigningHistory(objectId);
      setSigningHistory(history);
    } catch (error) {
      console.error("Failed to load signing history:", error);
    }
  };

  // Load signing history on component mount
  useEffect(() => {
    loadSigningHistory();
  }, [objectId]);

  return (
    <>
      <Stack gap="lg" style={{ position: "relative" }}>
        <LoadingOverlay
          visible={isSaving || isLoading}
          zIndex={1000}
          overlayProps={{ blur: 2 }}
        />
        {/* Work Steps Table */}
        <Group justify="space-between" align="center">
          <Title order={5} c="blue">
            {getTranslation("TITLE_REALISED_WORK_STEPS") ||
              "Realised Work Steps"}
            {/* {headerInfo ? ` - ${headerInfo}` : ''} */}
          </Title>
          <Tooltip
            label={
              isEditingDisabled
                ? getDisabledTooltip()
                : getTranslation("LABEL_ADD_NEW") || "Add New"
            }
            withArrow
            disabled={!isEditingDisabled}
          >
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleAddWorkStep}
              size="sm"
              disabled={isEditingDisabled}
            >
              {getTranslation("LABEL_ADD_NEW") || "Add New"}
            </Button>
          </Tooltip>
        </Group>

        <Paper withBorder>
          {items.length === 0 ? (
            <Table striped highlightOnHover>
              {renderColGroup()}
              <Table.Thead>
                <Table.Tr
                  style={{ backgroundColor: headerColor, color: headerText }}
                >
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_COATING_TYPE_WORK_PHASE") ||
                      "Coating Type / Work Phase"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_UNIT") || "Unit"}
                  </Table.Th>
                  <Table.Th
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      backgroundColor: theme.colors.gray[3],
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_PLANNED") || "Planned"}
                  </Table.Th>
                  <Table.Th
                    colSpan={3}
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      background:
                        "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_REALISED") || "Realised"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_CHANGE") || "Change"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_UNIT_PRICE_EUR") || "Unit Price €"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_PRICE_EUR") || "Price €"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_COST_CHANGE_EUR") || "Cost Change €"}
                  </Table.Th>
                  <Table.Th
                    rowSpan={2}
                    style={{
                      verticalAlign: "middle",
                      borderLeft: "1px solid #ddd",
                      textAlign: "center",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_ACTIONS") || "Actions"}
                  </Table.Th>
                </Table.Tr>
                <Table.Tr
                  style={{ backgroundColor: headerColor, color: headerText }}
                >
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_AMOUNT") || "Amount"}
                  </Table.Th>
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_AREA") || "Area"}
                  </Table.Th>
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_TONNE") || "Tonne"}
                  </Table.Th>
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      background:
                        "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_AMOUNT") || "Amount"}
                  </Table.Th>
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      background:
                        "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_AREA") || "Area"}
                  </Table.Th>
                  <Table.Th
                    style={{
                      textAlign: "center",
                      borderLeft: "1px solid #ddd",
                      background:
                        "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                      color: headerText,
                    }}
                  >
                    {getTranslation("LABEL_TONNE") || "Tonne"}
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td colSpan={13}>
                    {isLoading ? (
                      <Text c="blue" ta="center" py="xl">
                        {getTranslation("LABEL_LOADING_WORK_STEPS") ||
                          "Loading work steps..."}
                      </Text>
                    ) : (
                      <Stack align="center" gap="xs" py="xl">
                        <Text c="blue" size="sm" fw={500}>
                          {getTranslation("LABEL_NO_WORK_STEPS_YET") ||
                            "No work steps added yet"}
                        </Text>
                        <Text c="dimmed" size="xs">
                          {getTranslation(
                            "LABEL_CLICK_ADD_NEW_FIRST_WORK_STEP",
                          ) || 'Click "Add New" to create your first work step'}
                        </Text>
                      </Stack>
                    )}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          ) : (
            <Accordion
              multiple
              defaultValue={items
                .filter((i) => i.isGroupHeader)
                .map((i) => i.name)}
            >
              {items.map((item, idx) => {
                if (!item.isGroupHeader) return null;

                const groupName = item.name;
                const groupItems = items.filter((_, i) => {
                  if (i <= idx) return false;
                  if (items[i].isGroupHeader) return false;
                  let gName = "";
                  for (let j = i - 1; j >= 0; j--) {
                    if (items[j].isGroupHeader) {
                      gName = items[j].name;
                      break;
                    }
                  }
                  return gName === groupName;
                });

                return (
                  <Accordion.Item
                    key={`accordion-${groupName}`}
                    value={groupName}
                  >
                    <Accordion.Control
                      style={{
                        fontWeight: 600,
                        color: theme.colors.blue[6],
                        fontSize: "0.875rem",
                      }}
                    >
                      {groupName}
                    </Accordion.Control>
                    <Accordion.Panel>
                      <Table striped highlightOnHover>
                        {renderColGroup()}
                        <Table.Thead>
                          <Table.Tr
                            style={{
                              backgroundColor: headerColor,
                              color: headerText,
                            }}
                          >
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation(
                                "LABEL_COATING_TYPE_WORK_PHASE",
                              ) || "Coating Type / Work Phase"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_UNIT") || "Unit"}
                            </Table.Th>
                            <Table.Th
                              colSpan={3}
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                backgroundColor: theme.colors.gray[3],
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_PLANNED") || "Planned"}
                            </Table.Th>
                            <Table.Th
                              colSpan={3}
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                background:
                                  groupName === "Others"
                                    ? theme.colors.gray[3]
                                    : "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_REALISED") || "Realised"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_CHANGE") || "Change"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_UNIT_PRICE_EUR") ||
                                "Unit Price €"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_PRICE_EUR") || "Price €"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_COST_CHANGE_EUR") ||
                                "Cost Change €"}
                            </Table.Th>
                            <Table.Th
                              rowSpan={2}
                              style={{
                                verticalAlign: "middle",
                                borderLeft: "1px solid #ddd",
                                textAlign: "center",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_ACTIONS") || "Actions"}
                            </Table.Th>
                          </Table.Tr>
                          <Table.Tr
                            style={{
                              backgroundColor: headerColor,
                              color: headerText,
                            }}
                          >
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_AMOUNT") || "Amount"}
                            </Table.Th>
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_AREA") || "Area"}
                            </Table.Th>
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_TONNE") || "Tonne"}
                            </Table.Th>
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                background:
                                  groupName === "Others"
                                    ? headerColor
                                    : "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_AMOUNT") || "Amount"}
                            </Table.Th>
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                background:
                                  groupName === "Others"
                                    ? headerColor
                                    : "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_AREA") || "Area"}
                            </Table.Th>
                            <Table.Th
                              style={{
                                textAlign: "center",
                                borderLeft: "1px solid #ddd",
                                background:
                                  groupName === "Others"
                                    ? headerColor
                                    : "linear-gradient(to bottom, #dee2e6 0%, #ced4da 100%)",
                                color: headerText,
                              }}
                            >
                              {getTranslation("LABEL_TONNE") || "Tonne"}
                            </Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {groupItems.length === 0 ? (
                            <Table.Tr>
                              <Table.Td colSpan={13}>
                                <Text c="dimmed" ta="center">
                                  {getTranslation("LABEL_NO_ITEMS_IN_GROUP") ||
                                    "No items in this group"}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          ) : (
                            groupItems.map((groupItem, itemIdx) => (
                              <Table.Tr
                                key={`item-${groupItem.workItemId}-${groupItem.priceItemId}-${itemIdx}-${groupName}`}
                              >
                                <Table.Td>{groupItem.name}</Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "center",
                                  }}
                                >
                                  {groupItem.unitName}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                  }}
                                >
                                  {groupItem.plannedAmount === 0
                                    ? "-"
                                    : groupItem.plannedAmount.toFixed(2)}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                  }}
                                >
                                  {groupItem.plannedArea?.toFixed(2) ?? "-"}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                  }}
                                >
                                  {groupItem.plannedWeight?.toFixed(2) ?? "-"}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                    padding: "4px 8px",
                                    background:
                                      groupName === "Others"
                                        ? "transparent"
                                        : "linear-gradient(to bottom, #f1f3f5 0%, #e9ecef 100%)",
                                  }}
                                  onClick={() =>
                                    !isEditingDisabled &&
                                    groupItem.priceItemId &&
                                    setEditingCell({
                                      priceItemId: groupItem.priceItemId,
                                      field: "realisedAmount",
                                      value: getPendingValue(
                                        groupItem.priceItemId,
                                        "realisedAmount",
                                        groupItem.realisedAmount,
                                      ),
                                    })
                                  }
                                >
                                  {editingCell?.priceItemId ===
                                    groupItem.priceItemId &&
                                  editingCell?.field === "realisedAmount" ? (
                                    <NumberInput
                                      value={editingCell.value}
                                      onChange={(val) => {
                                        setEditingCell({
                                          ...editingCell,
                                          value: Number(val) || 0,
                                        });
                                        // Clear validation error when user starts typing
                                        if (
                                          groupItem.priceItemId &&
                                          validationErrors.has(
                                            groupItem.priceItemId,
                                          )
                                        ) {
                                          const newErrors = new Set(
                                            validationErrors,
                                          );
                                          newErrors.delete(
                                            groupItem.priceItemId,
                                          );
                                          setValidationErrors(newErrors);
                                        }
                                      }}
                                      onBlur={() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedAmount",
                                          groupItem.realisedAmount,
                                        );
                                        if (
                                          editingCell.value !== displayValue
                                        ) {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedAmount",
                                            editingCell.value,
                                          );
                                        } else {
                                          setEditingCell(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedAmount",
                                            editingCell.value,
                                          );
                                        } else if (e.key === "Escape") {
                                          setEditingCell(null);
                                        }
                                      }}
                                      autoFocus
                                      size="xs"
                                      decimalScale={2}
                                      hideControls
                                      error={
                                        groupItem.priceItemId &&
                                        validationErrors.has(
                                          groupItem.priceItemId,
                                        )
                                      }
                                      styles={{
                                        input: {
                                          textAlign: "right",
                                          borderColor:
                                            groupItem.priceItemId &&
                                            validationErrors.has(
                                              groupItem.priceItemId,
                                            )
                                              ? theme.colors.red[6]
                                              : undefined,
                                        },
                                      }}
                                      disabled={isEditingDisabled}
                                    />
                                  ) : (
                                    <span
                                      style={{
                                        cursor: !isEditingDisabled
                                          ? "pointer"
                                          : "default",
                                        opacity: isEditingDisabled ? 0.6 : 1,
                                        display: "block",
                                        padding: "4px",
                                        backgroundColor:
                                          groupItem.priceItemId &&
                                          validationErrors.has(
                                            groupItem.priceItemId,
                                          )
                                            ? theme.colors.red[1]
                                            : hasPendingChanges(
                                                  groupItem.priceItemId,
                                                  groupItem.workItemId,
                                                )
                                              ? theme.colors.yellow[1]
                                              : "transparent",
                                        border:
                                          groupItem.priceItemId &&
                                          validationErrors.has(
                                            groupItem.priceItemId,
                                          )
                                            ? `2px solid ${theme.colors.red[6]}`
                                            : "none",
                                      }}
                                    >
                                      {(() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedAmount",
                                          groupItem.realisedAmount,
                                        );
                                        return displayValue === 0
                                          ? "-"
                                          : displayValue.toFixed(2);
                                      })()}
                                    </span>
                                  )}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                    padding: "4px 8px",
                                    background:
                                      groupName === "Others"
                                        ? "transparent"
                                        : "linear-gradient(to bottom, #f1f3f5 0%, #e9ecef 100%)",
                                  }}
                                  onClick={() =>
                                    !isEditingDisabled &&
                                    groupItem.priceItemId &&
                                    setEditingCell({
                                      priceItemId: groupItem.priceItemId,
                                      field: "realisedArea",
                                      value: getPendingValue(
                                        groupItem.priceItemId,
                                        "realisedArea",
                                        groupItem.realisedArea ?? 0,
                                      ),
                                    })
                                  }
                                >
                                  {editingCell?.priceItemId ===
                                    groupItem.priceItemId &&
                                  editingCell?.field === "realisedArea" ? (
                                    <NumberInput
                                      value={editingCell.value}
                                      onChange={(val) =>
                                        setEditingCell({
                                          ...editingCell,
                                          value: Number(val) || 0,
                                        })
                                      }
                                      onBlur={() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedArea",
                                          groupItem.realisedArea ?? 0,
                                        );
                                        if (
                                          editingCell.value !== displayValue
                                        ) {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedArea",
                                            editingCell.value,
                                          );
                                        } else {
                                          setEditingCell(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedArea",
                                            editingCell.value,
                                          );
                                        } else if (e.key === "Escape") {
                                          setEditingCell(null);
                                        }
                                      }}
                                      autoFocus
                                      size="xs"
                                      decimalScale={2}
                                      hideControls
                                      styles={{ input: { textAlign: "right" } }}
                                      disabled={isEditingDisabled}
                                    />
                                  ) : (
                                    <span
                                      style={{
                                        cursor: !isEditingDisabled
                                          ? "pointer"
                                          : "default",
                                        opacity: isEditingDisabled ? 0.6 : 1,
                                        display: "block",
                                        padding: "4px",
                                        backgroundColor: hasPendingChanges(
                                          groupItem.priceItemId,
                                          groupItem.workItemId,
                                        )
                                          ? theme.colors.yellow[1]
                                          : "transparent",
                                      }}
                                    >
                                      {(() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedArea",
                                          groupItem.realisedArea ?? 0,
                                        );
                                        return displayValue === 0
                                          ? "-"
                                          : displayValue.toFixed(2);
                                      })()}
                                    </span>
                                  )}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                    padding: "4px 8px",
                                    background:
                                      groupName === "Others"
                                        ? "transparent"
                                        : "linear-gradient(to bottom, #f1f3f5 0%, #e9ecef 100%)",
                                  }}
                                  onClick={() =>
                                    !isEditingDisabled &&
                                    groupItem.priceItemId &&
                                    setEditingCell({
                                      priceItemId: groupItem.priceItemId,
                                      field: "realisedWeight",
                                      value: getPendingValue(
                                        groupItem.priceItemId,
                                        "realisedWeight",
                                        groupItem.realisedWeight ?? 0,
                                      ),
                                    })
                                  }
                                >
                                  {editingCell?.priceItemId ===
                                    groupItem.priceItemId &&
                                  editingCell?.field === "realisedWeight" ? (
                                    <NumberInput
                                      value={editingCell.value}
                                      onChange={(val) =>
                                        setEditingCell({
                                          ...editingCell,
                                          value: Number(val) || 0,
                                        })
                                      }
                                      onBlur={() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedWeight",
                                          groupItem.realisedWeight ?? 0,
                                        );
                                        if (
                                          editingCell.value !== displayValue
                                        ) {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedWeight",
                                            editingCell.value,
                                          );
                                        } else {
                                          setEditingCell(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          handleCellUpdate(
                                            groupItem,
                                            "realisedWeight",
                                            editingCell.value,
                                          );
                                        } else if (e.key === "Escape") {
                                          setEditingCell(null);
                                        }
                                      }}
                                      autoFocus
                                      size="xs"
                                      decimalScale={2}
                                      hideControls
                                      styles={{ input: { textAlign: "right" } }}
                                      disabled={isEditingDisabled}
                                    />
                                  ) : (
                                    <span
                                      style={{
                                        cursor: !isEditingDisabled
                                          ? "pointer"
                                          : "default",
                                        opacity: isEditingDisabled ? 0.6 : 1,
                                        display: "block",
                                        padding: "4px",
                                        backgroundColor: hasPendingChanges(
                                          groupItem.priceItemId,
                                          groupItem.workItemId,
                                        )
                                          ? theme.colors.yellow[1]
                                          : "transparent",
                                      }}
                                    >
                                      {(() => {
                                        const displayValue = getPendingValue(
                                          groupItem.priceItemId,
                                          "realisedWeight",
                                          groupItem.realisedWeight ?? 0,
                                        );
                                        return displayValue === 0
                                          ? "-"
                                          : displayValue.toFixed(2);
                                      })()}
                                    </span>
                                  )}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                    color:
                                      groupItem.change !== 0
                                        ? groupItem.change > 0
                                          ? theme.colors.green[7]
                                          : theme.colors.red[7]
                                        : undefined,
                                  }}
                                >
                                  {groupItem.change.toFixed(2)}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                  }}
                                >
                                  {groupItem.unitPrice.toFixed(2)}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                  }}
                                >
                                  {groupItem.price.toFixed(2)}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "right",
                                    color:
                                      groupItem.costChange !== 0
                                        ? groupItem.costChange > 0
                                          ? theme.colors.green[7]
                                          : theme.colors.red[7]
                                        : undefined,
                                  }}
                                >
                                  {groupItem.costChange.toFixed(2)}
                                </Table.Td>
                                <Table.Td
                                  style={{
                                    borderLeft: "1px solid #ddd",
                                    textAlign: "center",
                                  }}
                                >
                                  <Group gap="xs" justify="center">
                                    {hasPendingChanges(
                                      groupItem.priceItemId,
                                      groupItem.workItemId,
                                    ) && (
                                      <Tooltip
                                        label={
                                          getTranslation(
                                            "LABEL_DISCARD_CHANGES",
                                          ) || "Discard changes"
                                        }
                                        withArrow
                                      >
                                        <ActionIcon
                                          variant="subtle"
                                          color="gray"
                                          aria-label={
                                            getTranslation(
                                              "ARIA_DISCARD_CHANGES",
                                            ) || "Discard changes"
                                          }
                                          onClick={() =>
                                            groupItem.priceItemId &&
                                            discardRowChanges(
                                              groupItem.priceItemId,
                                            )
                                          }
                                        >
                                          <IconArrowBack size={16} />
                                        </ActionIcon>
                                      </Tooltip>
                                    )}
                                    {groupItem.workItemId > 0 ? (
                                      <>
                                        <Tooltip
                                          label={
                                            isEditingDisabled
                                              ? getDisabledTooltip()
                                              : getTranslation("LABEL_EDIT") ||
                                                "Edit"
                                          }
                                          withArrow
                                        >
                                          <ActionIcon
                                            variant="subtle"
                                            aria-label={
                                              getTranslation("ARIA_EDIT") ||
                                              "Edit"
                                            }
                                            onClick={() =>
                                              handleEditFetchedItem(groupItem)
                                            }
                                            disabled={isEditingDisabled}
                                            style={{
                                              cursor: isEditingDisabled
                                                ? "not-allowed"
                                                : "pointer",
                                            }}
                                          >
                                            <IconEdit size={16} />
                                          </ActionIcon>
                                        </Tooltip>
                                        <Popover
                                          position="bottom"
                                          withArrow
                                          shadow="md"
                                          opened={
                                            deletingWorkItemId ===
                                            groupItem.workItemId
                                          }
                                          onChange={(opened) => {
                                            if (!opened)
                                              setDeletingWorkItemId(null);
                                          }}
                                          trapFocus={false}
                                          closeOnClickOutside
                                          closeOnEscape
                                          clickOutsideEvents={[
                                            "mousedown",
                                            "touchstart",
                                          ]}
                                        >
                                          <Popover.Target>
                                            <div>
                                              <Tooltip
                                                label={
                                                  isEditingDisabled
                                                    ? getDisabledTooltip()
                                                    : getTranslation(
                                                        "LABEL_DELETE",
                                                      ) || "Delete"
                                                }
                                                withArrow
                                              >
                                                <ActionIcon
                                                  variant="subtle"
                                                  color="red"
                                                  aria-label={
                                                    getTranslation(
                                                      "ARIA_DELETE",
                                                    ) || "Delete"
                                                  }
                                                  onClick={() =>
                                                    confirmAndDeleteFetchedItem(
                                                      groupItem.workItemId,
                                                    )
                                                  }
                                                  disabled={isEditingDisabled}
                                                  style={{
                                                    cursor: isEditingDisabled
                                                      ? "not-allowed"
                                                      : "pointer",
                                                  }}
                                                >
                                                  <IconTrash size={16} />
                                                </ActionIcon>
                                              </Tooltip>
                                            </div>
                                          </Popover.Target>
                                          <Popover.Dropdown>
                                            <Group gap="xs">
                                              <Text size="xs" fw={500}>
                                                {getTranslation(
                                                  "LABEL_CONFIRM_SHORT",
                                                ) || "Sure?"}
                                              </Text>
                                              <Button
                                                size="xs"
                                                color="red"
                                                variant="filled"
                                                onClick={() =>
                                                  performDeleteWorkItem(
                                                    groupItem.workItemId,
                                                  )
                                                }
                                              >
                                                {getTranslation("LABEL_YES") ||
                                                  "Yes"}
                                              </Button>
                                              <Button
                                                size="xs"
                                                variant="default"
                                                onClick={() =>
                                                  setDeletingWorkItemId(null)
                                                }
                                              >
                                                {getTranslation("LABEL_NO") ||
                                                  "No"}
                                              </Button>
                                            </Group>
                                          </Popover.Dropdown>
                                        </Popover>
                                      </>
                                    ) : (
                                      <Tooltip
                                        label={
                                          groupName === "Others"
                                            ? getTranslation(
                                                "LABEL_CUSTOM_PLANNED_PHASE",
                                              ) || "Custom planned phase"
                                            : getTranslation(
                                                "LABEL_ADD_REALISED_PHASE",
                                              ) || "Add a realised phase"
                                        }
                                        withArrow
                                      >
                                        <Badge
                                          color="gray"
                                          variant="light"
                                          size="sm"
                                        >
                                          {groupName === "Others"
                                            ? getTranslation(
                                                "LABEL_OTHER_PLANNED",
                                              ) || "Other planned"
                                            : getTranslation(
                                                "LABEL_NOT_REALISED",
                                              ) || "Not realised"}
                                        </Badge>
                                      </Tooltip>
                                    )}
                                  </Group>
                                </Table.Td>
                              </Table.Tr>
                            ))
                          )}
                        </Table.Tbody>
                      </Table>
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          )}
        </Paper>

        {/* Totals Section */}
        {items.length > 0 && (
          <Paper
            withBorder
            p="md"
            style={{ backgroundColor: theme.colors.gray[0] }}
          >
            <Group justify="flex-end" gap="xl">
              <Group gap="xs">
                <Text fw={600}>
                  {getTranslation("LABEL_TOTAL_PRICE") || "Total Price:"}
                </Text>
                <Text fw={600} c="blue">
                  {calculateTotalCost().toFixed(2)} €
                </Text>
              </Group>
              <Group gap="xs">
                <Text fw={600}>
                  {getTranslation("LABEL_TOTAL_COST_CHANGE") ||
                    "Total Cost Change:"}
                </Text>
                <Text
                  fw={600}
                  c={
                    calculateTotalChange() !== 0
                      ? calculateTotalChange() > 0
                        ? "green"
                        : "red"
                      : undefined
                  }
                >
                  {calculateTotalChange().toFixed(2)} €
                </Text>
              </Group>
            </Group>
          </Paper>
        )}

        {/* Implemented Comment Field */}
        {canShowInternalComment && (
          <Textarea
            label={
              <span style={{ color: theme.colors.blue[6], fontWeight: 600 }}>
                {getTranslation("LABEL_COMMENT") || "Comment"}
              </span>
            }
            placeholder={
              getTranslation("PLACEHOLDER_ENTER_COMMENT") || "Enter comment"
            }
            minRows={4}
            disabled={!canModify || !canModifyWorkSteps}
            {...form.getInputProps("implementedComment")}
          />
        )}
        {/* Signing History Section - Visible to all users */}
        {signingHistory.length > 0 && (
          <Paper p="md" withBorder>
            <Group justify="space-between" mb="md">
              <Title order={6} c="blue">
                {getTranslation("TITLE_SIGNING_HISTORY") || "Signing History"}
              </Title>
              <ActionIcon
                variant="subtle"
                color="blue"
                onClick={loadSigningHistory}
                title={
                  getTranslation("TITLE_REFRESH_SIGNING_HISTORY") ||
                  "Refresh signing history"
                }
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Group>
            <Stack gap="xs">
              {signingHistory.map((record) => {
                // Determine which signer to show based on status
                const getSignerInfo = () => {
                  if (record.statusCode === 1 && record.primarySigner) {
                    return `${getTranslation("LABEL_WAITING_FOR") || "Waiting for:"} ${record.primarySigner}`;
                  } else if (
                    record.statusCode === 2 &&
                    record.secondarySigner
                  ) {
                    return `${getTranslation("LABEL_WAITING_FOR") || "Waiting for:"} ${record.secondarySigner}`;
                  }
                  return null;
                };

                const signerInfo = getSignerInfo();

                return (
                  <Paper key={record.id} p="xs" withBorder bg="gray.0">
                    <Group justify="space-between" align="flex-start">
                      <Stack gap={4}>
                        <Text size="xs" fw={500}>
                          {new Date(record.created).toLocaleString()}
                        </Text>
                        {signerInfo && (
                          <Text size="xs" c="blue" fw={500}>
                            {signerInfo}
                          </Text>
                        )}
                        {record.releaseTimestamp && (
                          <Text size="xs" c="orange" fw={500}>
                            {getTranslation("LABEL_RELEASED") || "Released:"}{" "}
                            {new Date(record.releaseTimestamp).toLocaleString()}
                          </Text>
                        )}
                        {record.releaseComment && (
                          <Text size="xs" c="dimmed" fs="italic">
                            "{record.releaseComment}"
                          </Text>
                        )}
                      </Stack>
                      <Group gap="xs">
                        <Badge
                          color={
                            record.statusCode === 99
                              ? "green"
                              : record.statusCode === 4
                                ? "orange"
                                : "blue"
                          }
                        >
                          {record.status}
                        </Badge>
                        {record.hasSignedFiles && record.statusCode === 99 && (
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            size="sm"
                            onClick={async () => {
                              try {
                                const blob = await downloadSignedFileForUser(
                                  record.id,
                                );
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = url;
                                link.download =
                                  record.signedFileName ||
                                  `signed_document_${record.id}.pdf`;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(url);
                                notifications.show({
                                  title:
                                    getTranslation("TITLE_SUCCESS") ||
                                    "Success",
                                  message:
                                    getTranslation(
                                      "MESSAGE_SIGNED_DOC_DOWNLOADED",
                                    ) ||
                                    "Signed document downloaded successfully",
                                  color: "green",
                                });
                              } catch (error) {
                                console.error(
                                  "Error downloading signed file:",
                                  error,
                                );
                                notifications.show({
                                  title:
                                    getTranslation("TITLE_ERROR") || "Error",
                                  message:
                                    getTranslation(
                                      "MESSAGE_SIGNED_DOC_DOWNLOAD_FAILED",
                                    ) || "Failed to download signed document",
                                  color: "red",
                                });
                              }
                            }}
                            title={
                              getTranslation(
                                "TITLE_DOWNLOAD_SIGNED_DOCUMENT",
                              ) || "Download signed document"
                            }
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        )}

        {/* Action Buttons */}
        <Group justify="space-between">
          <Group>
            <Button
              variant="outline"
              leftSection={<IconPrinter size={16} />}
              onClick={printMeasurementReport}
              disabled={items.length === 0}
            >
              {getTranslation("LABEL_PRINT_MEASUREMENT_REPORT") ||
                "Print Measurement Report"}
            </Button>
            {signingLocked ? (
              <Button
                variant="outline"
                color="orange"
                leftSection={<IconLockOpen size={16} />}
                onClick={() => setReleaseModalOpened(true)}
                disabled={!canEnableSigning}
              >
                {getTranslation("LABEL_RELEASE_SIGNING") || "Release Signing"}
              </Button>
            ) : (
              <Button
                variant="filled"
                color="blue"
                leftSection={<IconSignature size={16} />}
                onClick={handleSignDocumentPrecheck}
                disabled={
                  items.length === 0 || !canEnableSigning || !canModifyWorkSteps
                }
              >
                {getTranslation("LABEL_SIGN_DOCUMENT") || "Sign Document"}
              </Button>
            )}
          </Group>
          <Group>
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconArrowBack size={16} />}
              onClick={() => {
                setPendingChanges(new Map());
                setValidationErrors(new Set());
                // Reset comment field to original value
                form.setFieldValue("implementedComment", originalComment);
              }}
            >
              {/* Discard All Changes {pendingChanges.size > 0 && `(${pendingChanges.size})`} */}
              {getTranslation("LABEL_DISCARD_UNSAVED_CHANGES") ||
                "Discard Unsaved Changes"}
            </Button>
            <Button
              onClick={saveData}
              loading={isSaving}
              disabled={!canModify || isSaving || !canModifyWorkSteps}
            >
              {getTranslation("LABEL_SAVE") || "Save"}
            </Button>
          </Group>
        </Group>
      </Stack>

      {/* Add/Edit Work Step Modal */}
      <AddEditWorkStepModal
        opened={workStepModalOpened}
        onClose={() => setWorkStepModalOpened(false)}
        editingWorkStep={editingWorkStep}
        onSave={handleSaveWorkStep}
        contractId={contractId}
        objectId={objectId}
        workItemType={1} // Realised work items
        editingFetchedItem={editingFetchedItem}
        isOtherOperation={isEditingOther}
      />

      {/* Signing Modal */}
      <Modal
        opened={signingModalOpened}
        onClose={() => setSigningModalOpened(false)}
        title={
          getTranslation("TITLE_SIGN_MEASUREMENT_REPORT") ||
          "Sign Measurement Report"
        }
        size="lg"
        radius="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
        styles={{
          header: {
            background: "linear-gradient(to bottom, #f0f0f0 0%, #e5e5e5 100%)",
            borderBottom: "1px solid #d0d0d0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
            minHeight: "40px",
            padding: "16px",
          },
          title: {
            fontWeight: "bold",
            color: "#1976d2",
          },
          body: {
            padding: "16px",
          },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {getTranslation("LABEL_SIGNING_SEND_DESCRIPTION") ||
              "This will send the measurement report for electronic signature via SignSpace."}
          </Text>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                {getTranslation("LABEL_PRIMARY_SIGNER_CONTRACTOR_FOREMAN") ||
                  "Primary Signer (Contractor Foreman)"}
              </Text>
              <TextInput
                label={getTranslation("LABEL_NAME") || "Name"}
                value={
                  form?.values?.contractorForeman?.name ||
                  form?.values?.contractorForemanName ||
                  ""
                }
                disabled
              />
              <TextInput
                label={getTranslation("LABEL_EMAIL") || "Email"}
                value={
                  form?.values?.contractorForeman?.email ||
                  form?.values?.contractorForemanEmail ||
                  ""
                }
                disabled
              />
            </Stack>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                {getTranslation("LABEL_SECONDARY_SIGNER_FOREMAN") ||
                  "Secondary Signer (Foreman)"}
              </Text>
              <TextInput
                label={getTranslation("LABEL_NAME") || "Name"}
                value={
                  form?.values?.foreman?.name || form?.values?.foremanName || ""
                }
                disabled
              />
              <TextInput
                label={getTranslation("LABEL_EMAIL") || "Email"}
                value={
                  form?.values?.foreman?.email ||
                  form?.values?.foremanEmail ||
                  ""
                }
                disabled
              />
            </Stack>
          </Paper>

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => setSigningModalOpened(false)}
            >
              {getTranslation("LABEL_CANCEL") || "Cancel"}
            </Button>
            <Button
              onClick={handleSignDocument}
              loading={isSigning}
              disabled={isSigning}
              leftSection={<IconSignature size={16} />}
            >
              {getTranslation("LABEL_SEND_FOR_SIGNING") || "Send for Signing"}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Release Signing Modal */}
      <Modal
        opened={releaseModalOpened}
        onClose={() => {
          setReleaseModalOpened(false);
          setReleaseComment("");
        }}
        title={
          getTranslation("TITLE_RELEASE_SIGNING_LOCK") || "Release Signing Lock"
        }
        size="md"
        radius="md"
        closeOnClickOutside={false}
        closeOnEscape={false}
        styles={{
          header: {
            background: "linear-gradient(to bottom, #f0f0f0 0%, #e5e5e5 100%)",
            borderBottom: "1px solid #d0d0d0",
            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.15)",
            minHeight: "40px",
            padding: "16px",
          },
          title: {
            fontWeight: "bold",
            color: "#1976d2",
          },
          body: {
            padding: "16px",
          },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {getTranslation("LABEL_RELEASE_SIGNING_DESCRIPTION") ||
              "This will cancel the active signing request and unlock the object for editing."}
          </Text>

          <Textarea
            label={getTranslation("LABEL_COMMENT") || "Comment"}
            placeholder={
              getTranslation(
                "PLACEHOLDER_PLEASE_EXPLAIN_WHY_THE_SIGNING_IS_BEING_RELEASED",
              ) || "Please explain why the signing is being released..."
            }
            value={releaseComment}
            onChange={(event) => setReleaseComment(event.currentTarget.value)}
            required
            minRows={3}
            error={
              releaseComment.trim() === ""
                ? getTranslation("MESSAGE_COMMENT_REQUIRED") ||
                  "Comment is required"
                : null
            }
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setReleaseModalOpened(false);
                setReleaseComment("");
              }}
            >
              {getTranslation("LABEL_CANCEL") || "Cancel"}
            </Button>
            <Button
              onClick={handleReleaseSigning}
              loading={isReleasing}
              disabled={
                isReleasing || !releaseComment.trim() || !canEnableSigning
              }
              color="orange"
              leftSection={<IconLockOpen size={16} />}
            >
              {getTranslation("LABEL_RELEASE_SIGNING") || "Release Signing"}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
